/**
 * Story 成片站内公开（本人）与公开观看（游客可用）。
 * 公开媒体是普通同源地址，播放器直接用作 video src；未公开的集服务端一律 404。
 */

/**
 * @param {{ request: Function }} transport
 * @param {{ baseUrl?: string }} [options]
 */
export function createStoryFilmPublicApi(transport, { baseUrl = '' } = {}) {
  if (!transport || typeof transport.request !== 'function') throw new Error('createStoryFilmPublicApi: transport.request is required')
  return {
    /** 本人已公开的集。 */
    listMyPublicEpisodes() {
      return transport.request('GET', '/api/visual-game/me/story-film/public-episodes', {
        parse: value => {
          if (!isObject(value) || !Array.isArray(value.episode_ids)) throw new Error('Story film public episodes response is invalid')
          return value.episode_ids.map(id => requireId(id))
        },
      })
    },
    /**
     * 设置本集站内可见范围。
     * @param {{ episode_id: string, visibility: 'public'|'private' }} body
     */
    setVisibility({ episode_id, visibility }) {
      if (visibility !== 'public' && visibility !== 'private') throw new Error('Story film visibility must be public or private')
      return transport.request('POST', `/api/visual-game/me/story-film/episodes/${encodeURIComponent(requireId(episode_id))}/visibility`, {
        body: { visibility },
        parse: value => {
          if (!isObject(value) || (value.visibility !== 'public' && value.visibility !== 'private')) throw new Error('Story film visibility response is invalid')
          return { episode_id: requireId(value.episode_id), visibility: value.visibility }
        },
      })
    },
    /** S16：本集各渠道最近一次社媒分发记录。 @param {string} episodeId */
    listPublications(episodeId) {
      return transport.request('GET', `/api/visual-game/me/story-film/episodes/${encodeURIComponent(requireId(episodeId))}/publications`, {
        parse: value => {
          if (!isObject(value) || !Array.isArray(value.publications)) throw new Error('Story film publications response is invalid')
          return value.publications.map(item => ({ request_id: requireId(item.request_id), channel_id: requireId(item.channel_id), platform: String(item.platform), created_at: String(item.created_at) }))
        },
      })
    },
    /**
     * S13 提交后记下一次社媒分发。
     * @param {{ episode_id: string, request_id: string, channel_id: string, platform: string }} body
     */
    recordPublication({ episode_id, request_id, channel_id, platform }) {
      return transport.request('POST', `/api/visual-game/me/story-film/episodes/${encodeURIComponent(requireId(episode_id))}/publications`, {
        body: { request_id: requireId(request_id), channel_id: requireId(channel_id), platform: String(platform) },
        parse: value => ({ request_id: requireId(value?.request_id) }),
      })
    },
    /** S17：已公开的玩家短剧，按部聚合。 */
    listDramas() {
      return transport.request('GET', '/api/visual-game/story-film/public/dramas', {
        parse: value => {
          if (!isObject(value) || !Array.isArray(value.dramas)) throw new Error('Story film public dramas response is invalid')
          return value.dramas.map(parseDramaCard)
        },
      })
    },
    /** S18：一部短剧的公开季集。 @param {string} filmId */
    getDrama(filmId) {
      return transport.request('GET', `/api/visual-game/story-film/public/dramas/${encodeURIComponent(requireId(filmId))}`, {
        parse: value => parseDrama(value?.drama),
      })
    },
    /** 公开集的媒体地址。 @param {string} episodeId */
    mediaUrl(episodeId) {
      // baseUrl 可能是 ''、'/' 或带尾斜杠的完整地址，去掉尾斜杠再拼，避免出现 //api 被当成主机名。
      return `${baseUrl.replace(/\/+$/, '')}/api/visual-game/story-film/public/episodes/${encodeURIComponent(requireId(episodeId))}/media`
    },
  }
}

function parseDramaCard(value) {
  if (!isObject(value)) throw new Error('Story film public drama is invalid')
  return {
    film_id: requireId(value.film_id),
    title: String(value.title ?? ''),
    source_title: String(value.source_title ?? ''),
    source_slug: String(value.source_slug ?? ''),
    player_name: value.player_name === null ? null : String(value.player_name),
    episode_count: Number(value.episode_count),
    latest_season_no: Number(value.latest_season_no),
    updated_at: String(value.updated_at),
  }
}

function parseDrama(value) {
  if (!isObject(value) || !Array.isArray(value.seasons)) throw new Error('Story film public drama detail is invalid')
  return {
    film_id: requireId(value.film_id),
    title: String(value.title ?? ''),
    source_title: String(value.source_title ?? ''),
    source_slug: String(value.source_slug ?? ''),
    player_name: value.player_name === null ? null : String(value.player_name),
    seasons: value.seasons.map(season => ({
      season_no: Number(season.season_no),
      title: String(season.title ?? ''),
      episodes: season.episodes.map(episode => ({
        episode_id: requireId(episode.episode_id),
        episode_no: Number(episode.episode_no),
        title: episode.title === null ? null : String(episode.title),
        summary: episode.summary === null ? null : String(episode.summary),
        duration_ms: Number(episode.duration_ms),
        width: Number(episode.width),
        height: Number(episode.height),
      })),
    })),
  }
}

function requireId(value) {
  if (typeof value !== 'string' || value.trim() === '') throw new Error('Story film id is invalid')
  return value
}

function isObject(value) {
  return value !== null && typeof value === 'object'
}
